<template>
  <div class="animated fadeIn">

    <!-- Header -->
    <b-row class="mb-3 align-items-center">
      <b-col>
        <h4 class="mb-0"><i class="icon-list mr-2 text-primary"></i>Issue List</h4>
      </b-col>
      <b-col class="text-right">
        <b-button variant="danger" size="sm" to="/issue-tracker/create">
          <i class="icon-plus"></i> New Issue
        </b-button>
      </b-col>
    </b-row>

    <!-- Filter Bar -->
    <b-card class="mb-3 shadow-sm">
      <b-form @submit.prevent="applyFilters">
        <b-form-row>
          <b-col sm="6" md="3" lg="2" class="mb-2">
            <label class="filter-label">Status</label>
            <b-form-select v-model="filters.status" :options="statusOptions" size="sm" />
          </b-col>
          <b-col sm="6" md="3" lg="2" class="mb-2">
            <label class="filter-label">Priority</label>
            <b-form-select v-model="filters.priority" :options="priorityOptions" size="sm" />
          </b-col>
          <b-col sm="6" md="3" lg="2" class="mb-2">
            <label class="filter-label">Category</label>
            <b-form-select v-model="filters.category" :options="categoryOptions" size="sm" />
          </b-col>
          <b-col sm="6" md="3" lg="2" class="mb-2">
            <label class="filter-label">Client Type</label>
            <b-form-select v-model="filters.clientType" :options="clientTypeOptions" size="sm" />
          </b-col>
          <b-col sm="6" md="4" lg="2" class="mb-2">
            <label class="filter-label">Assigned To</label>
            <b-form-input v-model="filters.assignedTo" placeholder="username" size="sm" />
          </b-col>
          <b-col sm="6" md="4" lg="2" class="mb-2">
            <label class="filter-label">Search Title</label>
            <b-form-input v-model="filters.search" placeholder="keyword…" size="sm" />
          </b-col>
          <b-col sm="6" md="4" lg="2" class="mb-2">
            <label class="filter-label">From Date</label>
            <b-form-input v-model="filters.fromDate" type="date" size="sm" />
          </b-col>
          <b-col sm="6" md="4" lg="2" class="mb-2">
            <label class="filter-label">To Date</label>
            <b-form-input v-model="filters.toDate" type="date" size="sm" />
          </b-col>
          <b-col sm="12" md="4" lg="4" class="mb-2 d-flex align-items-end">
            <b-button type="submit" variant="primary" size="sm" class="mr-2" :disabled="loading">
              <i class="icon-magnifier mr-1"></i>Apply
            </b-button>
            <b-button type="button" variant="outline-secondary" size="sm" @click="clearFilters">
              <i class="icon-close mr-1"></i>Clear
            </b-button>
          </b-col>
        </b-form-row>
      </b-form>
    </b-card>

    <!-- Summary line -->
    <div class="d-flex justify-content-between align-items-center mb-2">
      <small class="text-muted">
        Showing <strong>{{ issues.length }}</strong> of <strong>{{ total }}</strong> issues
      </small>
      <b-spinner v-if="loading" small variant="primary" />
    </div>

    <!-- Table -->
    <b-card no-body class="shadow-sm">
      <b-table
        :items="issues"
        :fields="fields"
        :busy="loading"
        responsive striped hover show-empty
        empty-text="No issues found. Try adjusting your filters."
        class="mb-0 issue-table"
      >
        <template #table-busy>
          <div class="text-center py-4"><b-spinner variant="primary" /> Loading…</div>
        </template>
        <template #cell(id)="d">
          <span class="text-muted">#{{ d.value }}</span>
        </template>
        <template #cell(title)="d">
          <router-link :to="'/issue-tracker/' + d.item.id" class="issue-title-link">
            {{ d.value }}
          </router-link>
          <div class="text-muted" style="font-size:.75rem">{{ d.item.client_name }}</div>
        </template>
        <template #cell(priority)="d">
          <b-badge :variant="priorityVariant(d.value)" class="badge-pill px-2">{{ d.value }}</b-badge>
        </template>
        <template #cell(status)="d">
          <b-badge :variant="statusVariant(d.value)" class="badge-pill px-2">{{ d.value }}</b-badge>
        </template>
        <template #cell(client_type)="d">
          <b-badge variant="light" class="text-dark border">{{ d.value }}</b-badge>
        </template>
        <template #cell(assigned_name)="d">
          <span v-if="d.value" class="d-flex align-items-center">
            <span class="avatar-xs mr-1">{{ d.value.charAt(0) }}</span>
            <span>{{ d.value }}</span>
          </span>
          <span v-else class="text-muted small"><i class="icon-user"></i> Unassigned</span>
        </template>
        <template #cell(created_at)="d">
          <span style="font-size:.78rem">{{ d.value | fmtdate }}</span>
        </template>
        <template #cell(actions)="d">
          <b-button size="sm" variant="outline-primary" :to="'/issue-tracker/' + d.item.id">
            <i class="icon-eye"></i>
          </b-button>
        </template>
      </b-table>

      <!-- Pagination -->
      <div class="d-flex justify-content-center py-3" v-if="total > pageSize">
        <b-pagination
          v-model="currentPage"
          :total-rows="total"
          :per-page="pageSize"
          @change="onPageChange"
          size="sm"
          pills
          hide-goto-end-buttons
        />
      </div>
    </b-card>

  </div>
</template>

<script>
import issueService from '@/services/issueService'

export default {
  name: 'IssueList',
  filters: {
    fmtdate: function(v) { return v ? new Date(v).toLocaleDateString() : '—' }
  },
  data: function() {
    return {
      loading: false,
      issues: [],
      total: 0,
      currentPage: 1,
      pageSize: 20,
      filters: { status: '', priority: '', category: '', clientType: '', assignedTo: '', search: '', fromDate: '', toDate: '' },
      statusOptions: [
        { value: '', text: 'All Status' },
        { value: 'OPEN',        text: 'Open' },
        { value: 'IN_PROGRESS', text: 'In Progress' },
        { value: 'PENDING',     text: 'Pending' },
        { value: 'RESOLVED',    text: 'Resolved' },
        { value: 'CLOSED',      text: 'Closed' },
        { value: 'REOPENED',    text: 'Reopened' },
      ],
      priorityOptions: [
        { value: '', text: 'All Priority' },
        { value: 'CRITICAL', text: 'Critical' },
        { value: 'HIGH',     text: 'High' },
        { value: 'MEDIUM',   text: 'Medium' },
        { value: 'LOW',      text: 'Low' },
      ],
      categoryOptions: [
        { value: '', text: 'All Category' },
        { value: 'BUG',             text: 'Bug' },
        { value: 'FEATURE_REQUEST', text: 'Feature Request' },
        { value: 'SUPPORT',         text: 'Support' },
        { value: 'BILLING',         text: 'Billing' },
        { value: 'PERFORMANCE',     text: 'Performance' },
        { value: 'OTHER',           text: 'Other' },
      ],
      clientTypeOptions: [
        { value: '', text: 'All Clients' },
        { value: 'HRM',   text: 'HRM' },
        { value: 'CS',    text: 'CS' },
        { value: 'ADMIN', text: 'Admin' },
        { value: 'OTHER', text: 'Other' },
      ],
      fields: [
        { key: 'id',            label: 'ID',        sortable: false, thStyle: 'width:60px' },
        { key: 'title',         label: 'Issue',     sortable: false },
        { key: 'client_type',   label: 'Client',    sortable: false, thStyle: 'width:80px' },
        { key: 'priority',      label: 'Priority',  sortable: false, thStyle: 'width:100px' },
        { key: 'status',        label: 'Status',    sortable: false, thStyle: 'width:120px' },
        { key: 'assigned_name', label: 'Assignee',  sortable: false, thStyle: 'width:130px' },
        { key: 'created_at',    label: 'Created',   sortable: false, thStyle: 'width:100px' },
        { key: 'actions',       label: '',          thStyle: 'width:50px' },
      ]
    }
  },
  created: function() { this.fetchIssues() },
  methods: {
    buildParams: function() {
      return {
        status:     this.filters.status     || undefined,
        priority:   this.filters.priority   || undefined,
        category:   this.filters.category   || undefined,
        clientType: this.filters.clientType || undefined,
        assignedTo: this.filters.assignedTo || undefined,
        search:     this.filters.search     || undefined,
        fromDate:   this.filters.fromDate   || undefined,
        toDate:     this.filters.toDate     || undefined,
        page:       this.currentPage - 1,
        size:       this.pageSize
      }
    },
    fetchIssues: function() {
      var self = this
      self.loading = true
      issueService.getIssues(
        self.buildParams(),
        function(data) {
          self.issues  = data.items || []
          self.total   = data.total || 0
          self.loading = false
        },
        function(err) {
          self.loading = false
          self.$bvToast.toast(err, { title: 'Error', variant: 'danger', solid: true })
        }
      )
    },
    applyFilters: function() {
      this.currentPage = 1
      this.fetchIssues()
    },
    clearFilters: function() {
      this.filters = { status: '', priority: '', category: '', clientType: '', assignedTo: '', search: '', fromDate: '', toDate: '' }
      this.currentPage = 1
      this.fetchIssues()
    },
    onPageChange: function(page) {
      this.currentPage = page
      this.fetchIssues()
    },
    priorityVariant: function(p) {
      var map = { CRITICAL: 'danger', HIGH: 'warning', MEDIUM: 'info', LOW: 'secondary' }
      return map[p] || 'secondary'
    },
    statusVariant: function(s) {
      var map = { OPEN: 'primary', IN_PROGRESS: 'warning', PENDING: 'info',
                  RESOLVED: 'success', CLOSED: 'secondary', REOPENED: 'danger' }
      return map[s] || 'secondary'
    }
  }
}
</script>

<style scoped>
.filter-label { font-size: .72rem; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: #6c757d; margin-bottom: 2px; display: block; }
.issue-title-link { font-weight: 600; color: #343a40; }
.issue-title-link:hover { color: #007bff; }
.avatar-xs { width:22px; height:22px; border-radius:50%; background:#007bff; color:#fff;
             display:inline-flex; align-items:center; justify-content:center;
             font-size:.7rem; font-weight:700; flex-shrink:0; }
.issue-table td { vertical-align: middle; }
</style>
